import { useContext, useState, useEffect, useCallback, useRef } from 'react'
import { useSelector, useDispatch, shallowEqual } from 'react-redux'
import { createUseStyles } from 'react-jss'
import { Context as GeneralContext } from '@/context'
import { Context as ScrollbarContext } from '@/context/scrollbar'
import LocomotiveScroll from '@/components/LocomotiveScroll'
import Hero from '@/components/Hero'
import Footer from '@/components/Footer'
import Meta from '@/components/Meta'
import RevealGrid from '@/components/RevealGrid'
import ProductItem from '@/components/ProductItem'
import BtnLink from '@/components/BtnLink'
import useHandleAllMediaWithCb from '@/hooks/useHandleAllMediaWithCb'
import { cutExcerpt, insertBrAfterFirstSpace } from '@/utils/strings'
import actions from '@/actions'
import style from './style'

const useStyles = createUseStyles(style)

const ArchiveNews = ({
  cpt: cptFromProps,
  stub,
}) => {
  const { getImageUrl, setPageAnimationReady, setHeaderInverse } = useContext(GeneralContext)
  const classes = useStyles()
  const { scrollbar } = useContext(ScrollbarContext)
  const [load, setLoad] = useState(false)
  const [archive, setArchive] = useState([])
  const $root = useRef()
  const visibleNews = useRef(10)

  /*------------------------------
  Executes a callback on loading each image
  ------------------------------*/
  useHandleAllMediaWithCb({
    init: load,
    ref: $root.current?.ref,
  })

  /*------------------------------
  Redux Connect
  ------------------------------*/
  const { current, page, listPostsKey, listPostsObjects, newsPagination, postTotal, strings } = useSelector((state) => ({
    current: state.page.current,
    page: state.page.pages[stub],
    newsPagination: state.page.newsPagination,
    listPostsKey: state.page.archive[cptFromProps],
    listPostsObjects: state.page.posts,
    postTotal: state.page.archive.postTotal,
    postPage: state.page.archive.postPage,
    strings: state.language.string,
  }), shallowEqual)

  /*------------------------------
  Redux Actions
  ------------------------------*/
  const dispatch = useDispatch()
  const getPage = useCallback((index) => dispatch(actions.page.getDataPage(index)), [dispatch])
  const getListPost = useCallback((numItem, paged, category) => dispatch(actions.page.getListPost(numItem, paged, category)), [dispatch])
  const setCurrentPage = useCallback((index) => dispatch(actions.page.setCurrent(index)), [dispatch])
  const updatePageReady = useCallback((index) => dispatch(actions.loading.setReady(index)), [dispatch])
  const setSiteLoaded = useCallback(() => dispatch(actions.loading.setSiteLoaded()), [dispatch])
  const setNewsPagination = useCallback((number) => dispatch(actions.page.setNewsPagination(number)), [dispatch])

  /*------------------------------
  Complete Preload
  ------------------------------*/
  const completePreload = () => {
    updatePageReady(true)
    setSiteLoaded()
    setLoad(true)
  }

  /*------------------------------
  Initialize (API Calls)
  ------------------------------*/
  const initialize = () => {
    if (current !== stub) setCurrentPage(stub, 'pages')
    getPage(stub)
  }

  /*------------------------------
  If there are products & page is loaded from redux, complete the preload
  ------------------------------*/
  useEffect(() => {
    if (archive.length > 0 && page !== undefined) completePreload()
  }, [archive, page])

  /*------------------------------
  Map Categories to Product
  ------------------------------*/
  useEffect(() => {
    if (listPostsKey !== undefined) {
      const listMapped = listPostsKey.map((key) => {
        const post = listPostsObjects[key]
        return ({
          id: post.id,
          title: post.title.rendered,
          denomination: '',
          categories: post.categories,
          link: post.categories.includes(14) ? post.external_link : post.link,
          image: getImageUrl(post.featured_media, 'large', key, 'posts'),
        })
      })
      // Remove Category "Progetti" from Newsroom Page
      setArchive(listMapped.filter((l) => !l.categories.includes(13)))
    }
  }, [listPostsKey])

  /*------------------------------
  Did Mount
  ------------------------------*/
  useEffect(() => {
    if (!load) initialize()
  }, [])

  /*------------------------------
  Set Header Logo Styles
  ------------------------------*/
  useEffect(() => {
    setHeaderInverse(true)
    return () => setHeaderInverse(false)
  }, [])

  /*------------------------------
  Make API call everytime the newsPagination value changes
  ------------------------------*/
  useEffect(() => {
    if (newsPagination > 0) getListPost(visibleNews.current, newsPagination)
  }, [newsPagination])

  /*------------------------------
  Handle View More
  ------------------------------*/
  const handleViewMore = () => {
    setNewsPagination(newsPagination + 1)
  }

  /*------------------------------
  Update Scrollbar
  ------------------------------*/
  const updateScrollbar = () => {
    setTimeout(() => {
      if (scrollbar) scrollbar.update()
    }, 500)
  }

  /*------------------------------
  Update Scrollbar everytime listPostsKey length changed
  ------------------------------*/
  useEffect(() => {
    if (listPostsKey?.length > visibleNews.current) updateScrollbar()
  }, [listPostsKey])

  /*------------------------------
  Render Helmet
  ------------------------------*/
  const renderHelmet = () => {
    return load && (
      <Meta head_json={page.yoast_head} />
    )
  }

  /*------------------------------
  Render Heading
  ------------------------------*/
  const renderHeading = () => {
    return (
      <Hero
        aboveTheFold
        viewportHeight={false}
        background="light"
        fontSize="md"
        text={cutExcerpt(page.content.rendered)}
        title={insertBrAfterFirstSpace(page.title.rendered)}
        imgSize="crop"
        onComplete={() => setPageAnimationReady(true)}
      />
    )
  }

  /*------------------------------
  Render List
  ------------------------------*/
  const renderList = () => {
    return archive.length && (
      <RevealGrid
        cols={1}
        className={classes.listView}
      >
          {archive.map((item) => {
            return (
              <ProductItem
                key={item.id}
                item={item}
                layout="news"
                externalLink={item.categories.includes(14)}
              />
            )
          })}
      </RevealGrid>
    )
  }

  /*------------------------------
  Render View More
  ------------------------------*/
  const renderViewMore = () => {
    return Number(postTotal) > listPostsKey.length && (
      <div className={classes.wrapper}>
        <BtnLink
          onClick={handleViewMore}
          label={strings.loadmore_label}
          theme="dark"
        />
      </div>
    )
  }

  /*------------------------------
  Render Footer
  ------------------------------*/
  const renderFooter = () => <Footer />

  /*------------------------------
  Render Content
  ------------------------------*/
  const renderContent = () => {
    return load && (
      <>
        {renderHeading()}
        {renderList()}
        {renderViewMore()}
        {renderFooter()}
      </>
    )
  }

  return (
    <LocomotiveScroll
      init={load}
      className={`page pageArchiveNews ${classes.root}`}
      ref={$root}
    >
      {renderHelmet()}
      {renderContent()}
    </LocomotiveScroll>
  )
}

export default ArchiveNews
